import { useState, useEffect, useRef } from "react";
import type { FlowSection } from "../form-builder/types";
import ThankYouImageSelector from "./thank-you-image-selector";
import IconReplaceImage from "@/components/icons/icon-replace-image";
import ImageUploadField from "@/components/ui/image-upload-field";
import { useBrands, type BrandKit } from "@/modules/brand";

interface PreviewThankYouProps {
	id: string;
	section: FlowSection;
	brandName: string;
	brandLogo: string | null;
	brandkit: BrandKit;
	isSelected: boolean;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
	onImageUrlChange?: (imageUrl: string | null) => void;
	isDarkMode?: boolean;
}

export default function PreviewThankYou({ id, section, brandName, brandLogo, brandkit, isSelected, onTitleChange, onDescriptionChange, onImageUrlChange, isDarkMode = false }: PreviewThankYouProps) {
	void isSelected;
	void brandkit;

	const { selectedBrand } = useBrands();
	const showBranding = selectedBrand?.branding !== "disabled";

	const defaultImageUrl = "/images/img-thumbsup.png";
	const titlePlaceholder = "Thank you!";
	const descriptionPlaceholder = "Add a message...";

	// Track placeholder visibility (only re-render when empty state changes)
	const [isTitleEmpty, setIsTitleEmpty] = useState(!section.title?.trim());
	const [isDescriptionEmpty, setIsDescriptionEmpty] = useState(!section.description?.trim());

	const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
	const imageUploadFieldRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const descriptionRef = useRef<HTMLParagraphElement>(null);

	// Get current image URL
	const currentImageUrl = section.image_url || defaultImageUrl;

	// Determine if current image is uploaded (not predefined)
	const isUploadedImage = section.image_url && !section.image_url.startsWith("/images/img-");

	// Sync placeholder state when props change
	useEffect(() => {
		setIsTitleEmpty(!section.title?.trim());
		if (titleRef.current) {
			titleRef.current.textContent = section.title || "";
		}
	}, [section.title]);

	useEffect(() => {
		setIsDescriptionEmpty(!section.description?.trim());
		if (descriptionRef.current) {
			descriptionRef.current.textContent = section.description || "";
		}
	}, [section.description]);

	// Update placeholder visibility while typing (without re-rendering content)
	const handleTitleInput = (e: React.FormEvent<HTMLHeadingElement>) => {
		const newTitle = e.currentTarget.textContent || "";
		const isEmpty = !newTitle.trim();
		if (isEmpty !== isTitleEmpty) {
			setIsTitleEmpty(isEmpty);
		}
	};

	// Commit title change on blur
	const handleTitleBlur = (e: React.FormEvent<HTMLHeadingElement>) => {
		const newTitle = e.currentTarget.textContent || "";
		setIsTitleEmpty(!newTitle.trim());
		onTitleChange(newTitle);
	};

	// Update placeholder visibility while typing (without re-rendering content)
	const handleDescriptionInput = (e: React.FormEvent<HTMLParagraphElement>) => {
		const newDescription = e.currentTarget.textContent || "";
		const isEmpty = !newDescription.trim();
		if (isEmpty !== isDescriptionEmpty) {
			setIsDescriptionEmpty(isEmpty);
		}
	};

	// Commit description change on blur
	const handleDescriptionBlur = (e: React.FormEvent<HTMLParagraphElement>) => {
		const newDescription = e.currentTarget.textContent || "";
		setIsDescriptionEmpty(!newDescription.trim());
		onDescriptionChange(newDescription);
	};

	// Handle paste to strip formatting
	const handlePaste = (e: React.ClipboardEvent) => {
		e.preventDefault();
		const text = e.clipboardData.getData("text/plain");
		document.execCommand("insertText", false, text);
	};

	const handleImageClick = () => {
		if (onImageUrlChange) {
			setIsImageSelectorOpen(true);
		}
	};

	const handleImageConfirm = (imageUrl: string | null) => {
		if (onImageUrlChange) {
			onImageUrlChange(imageUrl);
		}
		setIsImageSelectorOpen(false);
	};

	const handleUploadClick = () => {
		// Find and click the ImageUploadField's clickable area or input
		if (imageUploadFieldRef.current) {
			const clickableElement = imageUploadFieldRef.current.querySelector('input[type="file"]') as HTMLInputElement;
			if (clickableElement) {
				// Add a one-time change listener to close the selector modal when a file is selected
				const handleFileSelect = () => {
					setIsImageSelectorOpen(false);
					clickableElement.removeEventListener("change", handleFileSelect);
				};
				clickableElement.addEventListener("change", handleFileSelect);
				clickableElement.click();
			} else {
				// Fallback: try clicking the container div
				const container = imageUploadFieldRef.current.querySelector('div[class*="cursor-pointer"]') as HTMLElement;
				if (container) {
					container.click();
				}
			}
		}
	};

	const handleImageUploadChange = (imageUrl: string | null) => {
		if (onImageUrlChange) {
			onImageUrlChange(imageUrl);
		}
	};

	return (
		<div id={id} className={`min-h-full flex flex-col items-center justify-center px-6 relative overflow-hidden ${isDarkMode ? "bg-gray-950" : "bg-white"}`}>
			{/* Text content container */}
			<div className="flex flex-col items-center">
				{/* Logo */}
				<div className="mb-6">
					{brandLogo ? (
						<img src={brandLogo} alt={brandName} className="h-10 md:h-12 w-auto object-contain" />
					) : (
						<span
							className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-primary"}`}
							style={{ fontFamily: "var(--font-body, inherit)", fontWeight: "var(--font-body-weight, 600)" }}
						>
							{brandName}
						</span>
					)}
				</div>

				{/* Title */}
				<div className="text-center relative min-w-full min-h-full">
					<h1
						ref={titleRef}
						className={`text-[36px] leading-tight tracking-tight outline-none rounded-sm transition-shadow cursor-text ${
							isDarkMode
								? `hover:ring-2 hover:ring-gray-600 focus:ring-2 focus:ring-brand/50 ${isTitleEmpty ? "text-transparent caret-white" : "text-white"}`
								: `hover:ring-2 hover:ring-gray-300 focus:ring-2 focus:ring-brand/50 ${isTitleEmpty ? "text-transparent caret-black" : "text-primary"}`
						}`}
						style={{ fontFamily: "var(--font-heading, inherit)", fontWeight: "var(--font-heading-weight, 600)", minHeight: "1.3em" }}
						contentEditable
						suppressContentEditableWarning
						onInput={handleTitleInput}
						onBlur={handleTitleBlur}
						onPaste={handlePaste}
					>
						{section.title || ""}
					</h1>
					{isTitleEmpty && (
						<span
							className={`absolute inset-0 text-[36px] leading-tight tracking-tight pointer-events-none whitespace-nowrap ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
							style={{ fontFamily: "var(--font-heading, inherit)", fontWeight: "var(--font-heading-weight, 600)" }}
						>
							{titlePlaceholder}
						</span>
					)}
				</div>

				{/* Subtitle */}
				<div className="text-center mt-3 max-w-md relative min-w-full min-h-full">
					<p
						ref={descriptionRef}
						className={`text-base md:text-lg leading-relaxed outline-none rounded-sm transition-shadow cursor-text ${
							isDarkMode
								? `hover:ring-2 hover:ring-gray-600 focus:ring-2 focus:ring-brand/50 ${isDescriptionEmpty ? "text-transparent caret-white" : "text-gray-300"}`
								: `hover:ring-2 hover:ring-gray-300 focus:ring-2 focus:ring-brand/50 ${isDescriptionEmpty ? "text-transparent caret-black" : "text-secondary"}`
						}`}
						style={{ fontFamily: "var(--font-body, inherit)", fontWeight: "var(--font-body-weight, 400)", minHeight: "1.5em" }}
						contentEditable
						suppressContentEditableWarning
						onInput={handleDescriptionInput}
						onBlur={handleDescriptionBlur}
						onPaste={handlePaste}
					>
						{section.description || ""}
					</p>
					{isDescriptionEmpty && (
						<span
							className={`absolute inset-0 text-base md:text-lg leading-relaxed pointer-events-none whitespace-nowrap ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
							style={{ fontFamily: "var(--font-body, inherit)", fontWeight: "var(--font-body-weight, 400)" }}
						>
							{descriptionPlaceholder}
						</span>
					)}
				</div>
			</div>

			{/* Celebration illustration */}
			{onImageUrlChange ? (
				<div className="w-full max-w-md mx-auto group cursor-pointer mt-10" onClick={handleImageClick}>
					<div className="relative">
						<img src={currentImageUrl} alt="" className="w-full h-auto transition-opacity duration-200 group-hover:opacity-50" />
						{/* Hover overlay with replace icon */}
						<div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
							<div className="p-3 rounded-lg bg-white/20 hover:bg-white/30 transition-colors">
								<IconReplaceImage className="w-8 h-8 [&_path]:fill-white" />
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className="w-full max-w-md mx-auto pointer-events-none mt-10">
					<img src={currentImageUrl} alt="" className="w-full h-auto" />
				</div>
			)}

			{/* Powered by text */}
			{showBranding && (
				<div className="mt-6">
					{/* <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
						Powered by{" "}
						<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
							Say About Us
						</a>
					</span> */}

					<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
						<img src="/images/badge-sayaboutus-2.svg" alt="build social proof with Say About Us" className="h-9 w-auto" />
					</a>
				</div>
			)}

			{/* Image Selector Dialog */}
			{onImageUrlChange && (
				<>
					<ThankYouImageSelector
						open={isImageSelectorOpen}
						currentImageUrl={section.image_url}
						onOpenChange={setIsImageSelectorOpen}
						onConfirm={handleImageConfirm}
						onUploadClick={handleUploadClick}
					/>
					{/* Hidden ImageUploadField for crop dialog - positioned off-screen but input remains clickable */}
					<div ref={imageUploadFieldRef} className="fixed -left-[9999px] -top-[9999px] opacity-0" style={{ pointerEvents: "none" }}>
						<div style={{ pointerEvents: "auto" }}>
							<ImageUploadField value={isUploadedImage && section.image_url ? section.image_url : null} onChange={handleImageUploadChange} label="" variant="light" maxSizeMB={5} />
						</div>
					</div>
				</>
			)}
		</div>
	);
}
